import { motion } from "framer-motion";
import { Cctv, ScanFace, DoorOpen, Wrench } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES } from "@/lib/site";

const handleSpotlight = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
};

const cardAnim = {
    initial: { opacity: 0, y: 32 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
};

const Tag = ({ children }) => (
    <span className="inline-block rounded-full border border-white/15 bg-white/5 px-3 py-1 font-hud text-[9px] uppercase tracking-[0.2em] text-slate-300">
        {children}
    </span>
);

const Services = () => {
    return (
        <section id="servicos" className="relative py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
                    <Reveal className="lg:col-span-8">
                        <Eyebrow>[ 01 — Serviços ]</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] text-white">
                            Tudo o que protege o seu condomínio
                            <span className="text-volt"> e a sua casa.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-4">
                        <p className="text-slate-400 leading-relaxed text-sm sm:text-base">
                            Da primeira vistoria ao suporte contínuo: um único
                            técnico responsável, do início ao fim do projeto.
                        </p>
                    </Reveal>
                </div>

                <div
                    data-testid="services-bento-grid"
                    className="grid lg:grid-cols-6 gap-4"
                >
                    <motion.div
                        {...cardAnim}
                        data-testid="service-card-cftv"
                        onMouseMove={handleSpotlight}
                        className="spotlight group relative overflow-hidden rounded-2xl border border-white/10 bg-panel lg:col-span-4 lg:min-h-[380px] hover:border-volt/40 transition-colors duration-300"
                    >
                        <div className="absolute right-0 top-0 h-full w-[46%] overflow-hidden">
                            <img
                                src={IMAGES.wallCameras}
                                alt="Câmeras de segurança CFTV instaladas"
                                loading="lazy"
                                className="h-full w-full object-cover grayscale contrast-110 transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-panel via-panel/60 to-transparent" />
                            <div className="scanline opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                        <div className="relative z-10 flex h-full flex-col justify-between p-7 lg:p-9 max-w-[62%]">
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-volt/30 bg-volt/10 text-volt">
                                <Cctv size={22} />
                            </span>
                            <div>
                                <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
                                    CFTV 4K &amp; visão noturna
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                    Câmeras IP 4K com visão noturna, gravação em
                                    local ou nuvem e imagem ao vivo no seu
                                    celular, de qualquer lugar.
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    <Tag>IP 4K</Tag>
                                    <Tag>Visão noturna</Tag>
                                    <Tag>Acesso remoto</Tag>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        {...cardAnim}
                        data-testid="service-card-facial-access"
                        onMouseMove={handleSpotlight}
                        className="spotlight group relative overflow-hidden rounded-2xl border border-white/10 bg-panel lg:col-span-2 lg:min-h-[380px] hover:border-volt/40 transition-colors duration-300"
                    >
                        <div className="relative h-44 overflow-hidden">
                            <img
                                src={IMAGES.facialAccess}
                                alt="Catraca com controle de acesso facial"
                                loading="lazy"
                                className="h-full w-full object-cover grayscale contrast-110 transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-panel to-transparent" />
                        </div>
                        <div className="relative z-10 p-7">
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-mint/30 bg-mint/10 text-mint">
                                <ScanFace size={22} />
                            </span>
                            <h3 className="mt-5 font-display text-xl font-bold text-white">
                                Controle de acesso facial
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                Moradores liberados em segundos, visitantes
                                registrados e portaria com histórico completo.
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                <Tag>Biométrico</Tag>
                                <Tag>Portaria</Tag>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        {...cardAnim}
                        data-testid="service-card-automation-gates"
                        onMouseMove={handleSpotlight}
                        className="spotlight group relative overflow-hidden rounded-2xl border border-white/10 bg-panel lg:col-span-2 lg:min-h-[300px] hover:border-volt/40 transition-colors duration-300"
                    >
                        <div className="absolute inset-0 grid-bg opacity-40" />
                        <div className="relative z-10 flex h-full flex-col justify-between p-7">
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-volt/30 bg-volt/10 text-volt">
                                <DoorOpen size={22} />
                            </span>
                            <div>
                                <h3 className="font-display text-xl font-bold text-white">
                                    Portões e automação
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                    Motores para portões deslizantes e de
                                    bater, acionamento por app e travas de
                                    segurança.
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    <Tag>Motor novo</Tag>
                                    <Tag>Retrofit</Tag>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        {...cardAnim}
                        data-testid="service-card-maintenance-support"
                        onMouseMove={handleSpotlight}
                        className="spotlight group relative overflow-hidden rounded-2xl border border-white/10 bg-panel lg:col-span-4 lg:min-h-[300px] hover:border-volt/40 transition-colors duration-300"
                    >
                        <div className="absolute right-0 top-0 h-full w-[40%] overflow-hidden">
                            <img
                                src={IMAGES.technician}
                                alt="Técnico realizando instalação de equipamento"
                                loading="lazy"
                                className="h-full w-full object-cover grayscale contrast-110 transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-panel via-panel/60 to-transparent" />
                        </div>
                        <div className="relative z-10 flex h-full flex-col justify-between p-7 lg:p-9 max-w-[62%]">
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-mint/30 bg-mint/10 text-mint">
                                <Wrench size={22} />
                            </span>
                            <div>
                                <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
                                    Suporte &amp; manutenção 24/7
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                    Manutenção preventiva, ajuste de imagens,
                                    troca de equipamentos e atendimento rápido
                                    quando você mais precisa.
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    <Tag>24/7</Tag>
                                    <Tag>Preventiva</Tag>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Services;
